import * as React from "react";
import { Button, ContentSwitcher, Switch, Toggle } from "carbon-components-react";
import { Maximize16, Minimize16 } from "@carbon/icons-react";
import { without } from "lodash";

import { EventsState } from "web_modules/Events/redux";
import { SlackEventShape, TRUSEventShape } from "web_modules/Events/types";

import SlackEvent from "./SlackEvent";
import TRUSEvent from "./TRUSEvent";
import { isHurdleNoteKind } from "./shared";

const styles = require("./styles.module.scss");

const localStorageKey = "notesAndEventsSettings";

const getSettings = () => {
  const ls = localStorage.getItem(localStorageKey);
  if (!ls) {
    return {};
  }
  const obj = JSON.parse(ls);
  if (typeof obj !== "object") {
    return {};
  }
  return obj;
};

const persistSetting = (k: string, v: any) => {
  const str = JSON.stringify({
    ...getSettings(),
    [k]: v,
  });
  localStorage.setItem(localStorageKey, str);
};

const sortedEventsToPinnedNoteId = (events): number => {
  for (let i = 0, l = events.length; i < l; i++) {
    const e = events[i];
    if (isHurdleNoteKind(e.kind) && e.fields && !!e.fields.pinned) {
      return e.id;
    }
  }
  return -1;
};

interface Props {
  appId: number;
  events: EventsState;
}

interface State {
  allEvents: (SlackEventShape | TRUSEventShape)[];
  expanded: number[];
  showOnlyNotes: boolean;
  showEngScope: boolean;
}

class Events extends React.Component<Props, State> {
  state = {
    allEvents: [],
    expanded: [],
    showOnlyNotes: false,
    showEngScope: false,
    ...getSettings(),
  };

  componentDidMount = () => {
    const {
      events: { slackEvents, TRUSEvents },
    } = this.props;

    const mergedEvents = [...slackEvents, ...TRUSEvents].filter(Boolean);
    const sortedEvents = mergedEvents.sort(
      (a, b) => Date.parse(b.created_at) - Date.parse(a.created_at),
    );

    this.setState(
      prevState => ({
        ...prevState,
        allEvents: sortedEvents,
      }),
      this.expandAllEvents,
    ); // expand all on load
  };

  toggleState = (attr: string) => {
    this.setState(
      prevState => ({
        ...prevState,
        [attr]: !prevState[attr],
      }),
      () => {
        persistSetting(attr, this.state[attr]);
      },
    );
  };

  toggleShowOnlyNotes = () => this.toggleState("showOnlyNotes");
  toggleShowEngScope = () => this.toggleState("showEngScope");

  expandAllEvents = () => {
    const { allEvents } = this.state;

    this.setState(prevState => ({
      ...prevState,
      expanded: allEvents.map(e => e.id),
    }));
  };

  collapseAllEvents = () => {
    this.setState(prevState => ({
      ...prevState,
      expanded: [],
    }));
  };

  toggleExpandCollapseEvent = (id: number) => {
    this.setState(prevState => {
      if (prevState.expanded.includes(id)) {
        return {
          ...prevState,
          expanded: without(prevState.expanded, id),
        };
      }
      return {
        ...prevState,
        expanded: [...prevState.expanded, id],
      };
    });
  };

  render() {
    const { appId, events } = this.props;
    const { showOnlyNotes, showEngScope, expanded, allEvents } = this.state;

    if (events.error) {
      return <p>Something went wrong: {events.error}</p>;
    }
    if (events.loading) {
      return <p>Loading…</p>;
    }

    const pinnedNoteId = sortedEventsToPinnedNoteId(allEvents);
    const shownEvents = allEvents.filter(e => {
      if (showOnlyNotes) {
        return isHurdleNoteKind(e.kind);
      }
      if (showEngScope) {
        return showEngScope;
      }
      if ((e.notify_scope && e.notify_scope === "eng") || (e.scope && e.scope === "eng")) {
        return false;
      }
      return true;
    });

    return (
      <div className={styles.events}>
        <div className={styles.controls}>
          <ContentSwitcher
            className={styles.switcher}
            selectedIndex={showOnlyNotes ? 1 : 0}
            onChange={this.toggleShowOnlyNotes}
          >
            <Switch text="Show everything" />
            <Switch text="Show only notes" />
          </ContentSwitcher>
          <div className={styles.rightSide}>
            <Toggle
              id="toggle-show-eng-items"
              toggled={showEngScope}
              onToggle={this.toggleShowEngScope}
              labelText="Show eng items?"
              labelA=""
              labelB=""
              size="sm"
              style={{ maxWidth: "180px" }}
            />
            <Button kind="secondary" onClick={this.expandAllEvents} renderIcon={Maximize16}>
              Expand all
            </Button>
            <Button kind="secondary" onClick={this.collapseAllEvents} renderIcon={Minimize16}>
              Collapse all
            </Button>
          </div>
        </div>
        {shownEvents.length === 0 ? (
          <p>Nothing to display</p>
        ) : (
          <ol>
            {shownEvents.map(e => {
              if (e.kind) {
                return (
                  <TRUSEvent
                    key={`trus-${e.id}`}
                    appId={appId}
                    event={e}
                    expanded={expanded.includes(e.id)}
                    highlightNotes={!showOnlyNotes}
                    isPinned={e.id === pinnedNoteId}
                    onClick={() => this.toggleExpandCollapseEvent(e.id)}
                  />
                );
              }
              return (
                <SlackEvent
                  key={`slack-${e.id}`}
                  appId={appId}
                  event={e}
                  expanded={expanded.includes(e.id)}
                  onClick={() => this.toggleExpandCollapseEvent(e.id)}
                />
              );
            })}
          </ol>
        )}
      </div>
    );
  }
}

export default Events;
